import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  QueryList,
  ViewChildren,
  Input,
  Inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-otp-dialog',
  templateUrl: './otp-dialog.component.html',
  styleUrls: ['./otp-dialog.component.scss'],
})
export class OtpDialogComponent {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  @Output() submitEvent = new EventEmitter<any>();
  @Output() resendOTPEvent = new EventEmitter<any>();
  @Input() email: string = '';

  otp: string[] = ['', '', '', '', '', ''];
  timer: number = 30;
  timerInterval: any;
  formType = 'default';

  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: any) {
    this.scrHeight = window.innerHeight;
    this.scrWidth = window.innerWidth;
  }

  scrHeight: number = window.innerHeight;
  scrWidth: number = window.innerWidth;

  isOtpValid(): boolean {
    return this.otp.every((digit) => digit.length === 1);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { email: string },
    private dialogRef: MatDialogRef<OtpDialogComponent>
  ) {
    this.email = data.email;
    this.getScreenSize();
  }

  ngOnInit() {
    this.startTimer();
  }

  startTimer() {
    // Clear any existing interval first
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timer = 30;
    this.timerInterval = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
      } else {
        // Clear interval when timer reaches 0
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  onInput(event: any, index: number) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Handle pasted content from Ctrl+V
    if (value.length > 1) {
      this.handlePastedContent(value);
      return;
    }

    // Ensure only numbers
    value = value.replace(/[^0-9]/g, '');

    // Update input value and OTP array
    input.value = value;
    this.otp[index] = value;

    // If we have a value and we're not at the last input, move to next
    if (value && index < 5) {
      const inputs = this.otpInputs.toArray();
      setTimeout(() => inputs[index + 1].nativeElement.focus(), 0);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const inputs = this.otpInputs.toArray();

    // Handle Ctrl+V
    if (event.key === 'v' && (event.ctrlKey || event.metaKey)) {
      return; // Let the input event handle the paste
    }

    if (event.key === 'Backspace') {
      this.otp[index] = '';
      // If we're not at the first input and the field is empty, go back
      if (index > 0 && !input.value) {
        setTimeout(() => inputs[index - 1].nativeElement.focus(), 0);
      }
      // Do not preventDefault, let the input event clear the field
      return;
    }

    // Allow only numbers and navigation keys
    if (
      !/^\d$/.test(event.key) && // Not a number
      !['Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(event.key) // Not a navigation key
    ) {
      event.preventDefault();
    }
  }

  handlePastedContent(content: string) {
    // Get only numbers from pasted content
    const numbers = content
      .replace(/[^0-9]/g, '')
      .split('')
      .slice(0, 6);

    const inputs = this.otpInputs.toArray();

    // Fill available inputs with pasted numbers
    numbers.forEach((num, index) => {
      if (index < 6) {
        this.otp[index] = num;
        inputs[index].nativeElement.value = num;
      }
    });

    // Focus the next empty input or the last input if all are filled
    const nextEmptyIndex = this.otp.findIndex((val) => !val);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputs[focusIndex].nativeElement.focus();
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text');

    if (!pastedData) return;
    this.handlePastedContent(pastedData);
  }

  resendOTP() {
    // Reset OTP fields
    this.otp = ['', '', '', '', '', ''];
    const inputs = this.otpInputs.toArray();
    inputs.forEach((input: ElementRef) => {
      input.nativeElement.value = '';
    });
    inputs[0].nativeElement.focus(); // Focus first input after reset

    // Reset and start timer
    this.startTimer();
    this.resendOTPEvent.emit();
  }

  verifyOTP(_event?: any) {
    const otpValue = this.otp.join('');
    if (otpValue.length === 6) {
      this.submitEvent.emit({
        otp: otpValue,
      });
    }
  }

  matDialogClose(_event?: any) {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
}
